import { useState, useEffect } from 'react';
import { Menu, X, Sun, Moon, Instagram, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTheme } from '@/contexts/ThemeContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [scrolled, setScrolled] = useState(false);
  
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location.hash]);

  const menuItems = [
    { name: 'خانه', href: '/', isExternal: false },
    { name: 'محصولات', href: '/products', isExternal: false },
    { name: 'درباره ما', href: '#about', isExternal: true },
    { name: 'بخش‌ها', href: '#departments', isExternal: true },
    { name: 'مشتریان', href: '#customers', isExternal: true },
    { name: 'مقالات', href: '/blog', isExternal: false },
    { name: 'تماس', href: '/contact', isExternal: false }
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('جستجو برای:', searchQuery);
    setIsSearchOpen(false);
    setSearchQuery('');
  };

  const handleSectionNavigation = (href: string) => {
    if (location.pathname !== '/') {
      navigate(`/${href}`);
    } else {
      const element = document.querySelector(href);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
      else window.location.hash = href;
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 w-full overflow-hidden ${
        scrolled 
          ? 'backdrop-blur-xl bg-white/90 dark:bg-steel-dark/90 shadow-2xl border-b border-copper/20' 
          : 'backdrop-blur-md bg-white/70 dark:bg-steel-dark/70'
      }`}
    >
      {/* Glassmorphism overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-white/80 via-white/60 to-white/80 dark:from-steel-dark/80 dark:via-steel-dark/60 dark:to-steel-dark/80"></div>
      
      <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 relative">
        <div className="flex items-center justify-between h-14 sm:h-16 md:h-20 w-full min-w-0">
          
          {/* Logo - Right Side (RTL) */}
          <Link to="/" aria-label="کیا الکتروموتور پارت؛ صفحه اصلی" className="flex items-center gap-2 sm:gap-3 md:gap-4 flex-shrink-0 min-w-0 max-w-[60%] sm:max-w-none">
            <div className="flex-shrink-0">
              <img 
                src="/images/kep-logo-small-v3.webp"
                srcSet="/images/kep-logo-small-v3.webp 96w, /images/kep-logo-v3.webp 160w"
                sizes="(min-width: 1024px) 80px, 48px"
                alt="KEP Logo" 
                width={160}
                height={160}
                className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 object-contain" 
              />
            </div>
            <div className="block min-w-0 flex-shrink overflow-hidden">
              <span className="text-xs sm:text-base md:text-lg lg:text-xl font-bold bg-gradient-to-r from-copper to-copper-dark bg-clip-text text-transparent truncate whitespace-nowrap">
                کیا الکترو موتور پارت
              </span>
              <p className="hidden sm:block text-xs sm:text-sm text-steel dark:text-white/70 font-medium truncate">
                تولید کننده الکترو موتورهای الکتریکی
              </p>
            </div>
          </Link>

          {/* Desktop Navigation - Center */}
          <nav className="hidden lg:flex items-center gap-4 xl:gap-6 flex-1 justify-center min-w-0 mx-4">
            {menuItems.map((item, index) => (
              <div key={item.name} className="header-fade-in">
                {item.isExternal ? (
                  <button 
                    onClick={() => handleSectionNavigation(item.href)}
                    className="relative group text-steel-dark dark:text-white hover:text-copper dark:hover:text-copper-light transition-all duration-300 font-semibold text-sm py-2 px-2 xl:px-3 rounded-lg hover:bg-copper/5 dark:hover:bg-copper/10 whitespace-nowrap"
                  >
                    {item.name}
                    <span className="absolute -bottom-1 left-1/2 h-0.5 w-0 -translate-x-1/2 bg-gradient-to-r from-copper to-copper-light transition-all duration-300 group-hover:w-full" />
                  </button>
                ) : (
                  <Link 
                    to={item.href}
                    className="relative group text-steel-dark dark:text-white hover:text-copper dark:hover:text-copper-light transition-all duration-300 font-semibold text-sm px-2 xl:px-3 py-2 rounded-lg hover:bg-copper/5 dark:hover:bg-copper/10 whitespace-nowrap"
                  >
                    {item.name}
                    <span className="absolute -bottom-1 left-1/2 h-0.5 w-0 -translate-x-1/2 bg-gradient-to-r from-copper to-copper-light transition-all duration-300 group-hover:w-full" />
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Action Buttons - Left Side (RTL) */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            
            {/* Search Button - Hidden on mobile */}
            <div className="relative hidden md:block">
              {isSearchOpen && (
                  <form
                    onSubmit={handleSearch}
                    className="absolute right-12 top-1/2 flex w-[280px] -translate-y-1/2 items-center overflow-hidden animate-in fade-in"
                  >
                    <Input 
                      type="text" 
                      placeholder="جستجو..." 
                      value={searchQuery} 
                      onChange={(e) => setSearchQuery(e.target.value)} 
                      className="h-10 rounded-full border-copper/30 focus:border-copper bg-white/90 dark:bg-steel-dark/90 text-steel-dark dark:text-white placeholder:text-steel dark:placeholder:text-steel-light" 
                      autoFocus 
                    />
                    <Button 
                      type="submit" 
                      size="sm" 
                      aria-label="ارسال جست‌وجو"
                      className="absolute left-1 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-copper hover:bg-copper-dark text-white p-0"
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                  </form>
                )}
            </div>

            {/* Instagram Button */}
            <Button 
              variant="ghost" 
              size="sm" 
              aria-label="صفحه اینستاگرام کیا الکتروموتور"
              title="اینستاگرام کیا الکتروموتور"
              className="p-1.5 sm:p-2 rounded-full hover:bg-copper/10 transition-all duration-300 hover:scale-110 flex-shrink-0"
            >
              <Instagram className="h-4 w-4 sm:h-5 sm:w-5 text-steel-dark dark:text-white" />
              <span className="sr-only">صفحه اینستاگرام کیا الکتروموتور</span>
            </Button>

            {/* Theme Toggle */}
            <Button 
              variant="ghost" 
              size="sm" 
              aria-label={theme === 'light' ? 'فعال‌کردن حالت شب' : 'فعال‌کردن حالت روز'}
              title={theme === 'light' ? 'فعال‌کردن حالت شب' : 'فعال‌کردن حالت روز'}
              onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
              className="p-1.5 sm:p-2 rounded-full hover:bg-copper/10 transition-all duration-300 hover:scale-110 relative overflow-hidden flex-shrink-0"
            >
              <div>
                {theme === 'light' ? (
                  <Moon className="h-4 w-4 sm:h-5 sm:w-5 text-steel-dark dark:text-white" />
                ) : (
                  <Sun className="h-4 w-4 sm:h-5 sm:w-5 text-copper-light" />
                )}
              </div>
              <span className="sr-only">{theme === 'light' ? 'فعال‌کردن حالت شب' : 'فعال‌کردن حالت روز'}</span>
            </Button>

            {/* Mobile Menu Button */}
            <Button 
              variant="ghost" 
              size="sm" 
              aria-label={isMobileMenuOpen ? 'بستن منو' : 'بازکردن منو'}
              className="lg:hidden p-1.5 sm:p-2 hover:bg-copper/10 transition-all duration-300 flex-shrink-0" 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <div>
                {isMobileMenuOpen ? (
                  <X className="h-5 w-5 sm:h-6 sm:w-6 text-steel-dark dark:text-white" />
                ) : (
                  <Menu className="h-5 w-5 sm:h-6 sm:w-6 text-steel-dark dark:text-white" />
                )}
              </div>
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
            <nav className="lg:hidden overflow-hidden border-t border-copper/20 bg-white/50 dark:bg-steel-dark/50 backdrop-blur-sm animate-in fade-in slide-in-from-top-2">
              <div className="py-4 space-y-1 px-2">
                {menuItems.map((item, index) => (
                  <div key={item.name}>
                    {item.isExternal ? (
                      <button 
                        onClick={() => handleSectionNavigation(item.href)}
                        className="block py-3 px-4 text-steel-dark dark:text-white hover:text-copper dark:hover:text-copper-light hover:bg-copper/10 dark:hover:bg-copper/20 rounded-lg transition-all duration-300 font-medium w-full text-right"
                      >
                        {item.name}
                      </button>
                    ) : (
                      <Link 
                        to={item.href}
                        className="block py-3 px-4 text-steel-dark dark:text-white hover:text-copper dark:hover:text-copper-light hover:bg-copper/10 dark:hover:bg-copper/20 rounded-lg transition-all duration-300 font-medium" 
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </nav>
          )}
      </div>
    </header> 
  );
};

export default Header;
